import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMap, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Slider } from './ui/slider';
import { toast } from 'sonner';
import {
  MapPin,
  Target,
  Navigation,
  Loader2,
  Check,
  AlertTriangle,
  Maximize2,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';

// Fix Leaflet default marker icon issue
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

// Custom marker icon
const createCustomIcon = (color = '#3b82f6') => new L.DivIcon({
  className: 'custom-marker',
  html: `
    <div style="
      background-color: ${color};
      width: 32px;
      height: 32px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 10px;
        height: 10px;
        background: white;
        border-radius: 50%;
        transform: rotate(45deg);
      "></div>
    </div>
  `,
  iconSize: [32, 32],
  iconAnchor: [16, 32],
  popupAnchor: [0, -32],
});

// Geofence radius presets
const RADIUS_PRESETS = [
  { value: 100, label: '100m', description: 'Compact site' },
  { value: 200, label: '200m', description: 'Default' },
  { value: 300, label: '300m', description: 'Medium site' },
  { value: 500, label: '500m', description: 'Large site' },
];

/**
 * Map Event Handler Component
 * Handles click events to place marker
 */
function MapClickHandler({ onLocationSelect, isPlacingMarker }) {
  useMapEvents({
    click: (e) => {
      if (isPlacingMarker) {
        onLocationSelect({
          lat: e.latlng.lat,
          lng: e.latlng.lng,
        });
      }
    },
  });
  return null;
}

/**
 * Map Center Updater Component
 * Updates map view when coordinates change with smooth animation
 */
function MapCenterUpdater({ center, zoom, animate = true }) {
  const map = useMap();
  
  useEffect(() => {
    if (center && center.lat && center.lng) {
      if (animate) {
        // Smooth fly-to animation for better UX
        map.flyTo([center.lat, center.lng], zoom || 18, {
          duration: 1.5,
        });
      } else {
        map.setView([center.lat, center.lng], zoom || map.getZoom());
      }
    }
  }, [center, zoom, animate, map]);
  
  return null;
}

/**
 * SiteGeofenceMap Component
 * 
 * Interactive map for site location and geofence configuration:
 * - Click to place marker
 * - Adjust geofence radius with presets or custom value
 * - Visual circle showing geofence boundary
 * - GPS location capture
 */
export function SiteGeofenceMap({
  latitude,
  longitude,
  geofenceRadius = 200,
  onLocationChange,
  onRadiusChange,
  readOnly = false,
  showControls = true,
  height = '350px',
  className = '',
}) {
  const [position, setPosition] = useState(
    latitude && longitude ? { lat: latitude, lng: longitude } : null
  );
  const [radius, setRadius] = useState(geofenceRadius);
  const [isPlacingMarker, setIsPlacingMarker] = useState(false);
  const [customRadius, setCustomRadius] = useState('');
  const [gpsLoading, setGpsLoading] = useState(false);
  const [mapZoom, setMapZoom] = useState(latitude && longitude ? 18 : 12);
  const [shouldAnimate, setShouldAnimate] = useState(false);
  
  // Default center (Sydney, Australia)
  const defaultCenter = { lat: -33.8688, lng: 151.2093 };
  const mapCenter = position || defaultCenter;
  
  // Sync external props - auto-zoom when coordinates are set from address selection
  useEffect(() => {
    if (latitude && longitude) {
      const newPosition = { lat: latitude, lng: longitude };
      const hasChanged = !position || 
        Math.abs(position.lat - latitude) > 0.0001 || 
        Math.abs(position.lng - longitude) > 0.0001;
      
      if (hasChanged) {
        setPosition(newPosition);
        setMapZoom(18); // Zoom to street level
        setShouldAnimate(true); // Enable animation for address selection
      }
    }
  }, [latitude, longitude]);
  
  // Reset animation flag after it triggers
  useEffect(() => {
    if (shouldAnimate) {
      const timer = setTimeout(() => setShouldAnimate(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [shouldAnimate]);
  
  useEffect(() => {
    setRadius(geofenceRadius);
  }, [geofenceRadius]);
  
  // Handle marker placement
  const handleLocationSelect = useCallback((coords) => {
    setPosition(coords);
    setIsPlacingMarker(false);
    onLocationChange?.(coords.lat, coords.lng);
    toast.success('Location set');
  }, [onLocationChange]);
  
  // Handle radius change
  const handleRadiusChange = useCallback((newRadius) => {
    setRadius(newRadius);
    onRadiusChange?.(newRadius);
  }, [onRadiusChange]);
  
  // Handle preset selection
  const handlePresetSelect = (presetValue) => {
    handleRadiusChange(presetValue);
    setCustomRadius('');
  };
  
  // Handle custom radius input
  const handleCustomRadiusSubmit = () => {
    const value = parseInt(customRadius, 10);
    if (value >= 50 && value <= 2000) {
      handleRadiusChange(value);
      toast.success(`Geofence radius set to ${value}m`);
    } else {
      toast.error('Radius must be between 50m and 2000m');
    }
  };
  
  // Get current GPS location
  const handleGetGPS = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }
    
    setGpsLoading(true);
    
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const coords = {
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
        };
        setPosition(coords);
        onLocationChange?.(coords.lat, coords.lng);
        setMapZoom(17);
        toast.success('GPS location captured');
        setGpsLoading(false);
      },
      (error) => {
        toast.error(`GPS Error: ${error.message}`);
        setGpsLoading(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0,
      }
    );
  };
  
  // Calculate geofence color based on radius
  const getGeofenceColor = () => {
    if (radius <= 100) return '#22c55e'; // Green - tight
    if (radius <= 200) return '#3b82f6'; // Blue - default
    if (radius <= 300) return '#f59e0b'; // Amber - medium
    return '#ef4444'; // Red - large
  };
  
  return (
    <div className={`space-y-4 ${className}`}>
      {/* Map Container */}
      <Card className="bg-[#18181b] border-[#3f3f46] overflow-hidden">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-rajdhani text-zinc-400 flex items-center justify-between">
            <span className="flex items-center gap-2">
              <MapPin className="w-4 h-4 text-blue-400" />
              SITE LOCATION & GEOFENCE
            </span>
            {position && (
              <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                <Check className="w-3 h-3 mr-1" />
                Location Set
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div style={{ height }} className="relative">
            <MapContainer
              center={[mapCenter.lat, mapCenter.lng]}
              zoom={mapZoom}
              style={{ height: '100%', width: '100%' }}
              scrollWheelZoom={true}
              className="z-0"
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              
              <MapCenterUpdater center={position} zoom={mapZoom} animate={shouldAnimate} />
              
              {!readOnly && (
                <MapClickHandler 
                  onLocationSelect={handleLocationSelect} 
                  isPlacingMarker={isPlacingMarker}
                />
              )}
              
              {/* Site Marker */}
              {position && (
                <>
                  <Marker 
                    position={[position.lat, position.lng]}
                    icon={createCustomIcon('#3b82f6')}
                  />
                  
                  {/* Geofence Circle */}
                  <Circle
                    center={[position.lat, position.lng]}
                    radius={radius}
                    pathOptions={{
                      color: getGeofenceColor(),
                      fillColor: getGeofenceColor(),
                      fillOpacity: 0.15,
                      weight: 2,
                      dashArray: '5, 5',
                    }}
                  />
                </>
              )}
            </MapContainer>
            
            {/* Map Overlay - Click instruction */}
            {isPlacingMarker && !readOnly && (
              <div className="absolute top-2 left-1/2 transform -translate-x-1/2 z-[1000]">
                <Badge className="bg-blue-500 text-white animate-pulse">
                  <Target className="w-3 h-3 mr-1" />
                  Click on map to place marker
                </Badge>
              </div>
            )}
            
            {/* Zoom Controls */}
            <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-1">
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0 bg-[#18181b]/90 border border-[#3f3f46]"
                onClick={() => setMapZoom(prev => Math.min(prev + 1, 19))}
              >
                <ZoomIn className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0 bg-[#18181b]/90 border border-[#3f3f46]"
                onClick={() => setMapZoom(prev => Math.max(prev - 1, 5))}
              >
                <ZoomOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Controls */}
      {showControls && !readOnly && (
        <div className="space-y-4">
          {/* Location Controls */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={isPlacingMarker ? 'default' : 'outline'}
              size="sm"
              onClick={() => setIsPlacingMarker(!isPlacingMarker)}
              className={isPlacingMarker ? 'bg-blue-500 hover:bg-blue-600' : 'border-[#3f3f46]'}
              data-testid="place-marker-btn"
            >
              <Target className="w-4 h-4 mr-2" />
              {isPlacingMarker ? 'Cancel' : 'Place Marker'}
            </Button>
            
            <Button
              variant="outline"
              size="sm"
              onClick={handleGetGPS}
              disabled={gpsLoading}
              className="border-[#3f3f46]"
              data-testid="get-gps-btn"
            >
              {gpsLoading ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Navigation className="w-4 h-4 mr-2" />
              )}
              Use Device GPS
            </Button>
          </div>
          
          {/* Coordinates Display */}
          {position && (
            <div className="flex flex-wrap gap-4 text-xs text-zinc-400 bg-[#09090b] p-3 rounded border border-[#3f3f46]">
              <span>
                <span className="text-zinc-500">Lat:</span>{' '}
                <span className="font-mono text-zinc-300">{position.lat.toFixed(6)}</span>
              </span>
              <span>
                <span className="text-zinc-500">Lng:</span>{' '}
                <span className="font-mono text-zinc-300">{position.lng.toFixed(6)}</span>
              </span>
            </div>
          )}
          
          {/* Geofence Radius Controls */}
          <Card className="bg-[#09090b] border-[#3f3f46]">
            <CardHeader className="pb-2 pt-3">
              <CardTitle className="text-xs font-rajdhani text-zinc-400 flex items-center gap-2">
                <Target className="w-3 h-3 text-amber-400" />
                GEOFENCE RADIUS
                <Badge className="ml-auto text-xs" style={{ 
                  backgroundColor: `${getGeofenceColor()}20`, 
                  color: getGeofenceColor(),
                  borderColor: `${getGeofenceColor()}50`,
                }}>
                  {radius}m
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Preset Buttons */}
              <div className="flex flex-wrap gap-2">
                {RADIUS_PRESETS.map((preset) => (
                  <Button
                    key={preset.value}
                    variant={radius === preset.value ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => handlePresetSelect(preset.value)}
                    className={radius === preset.value 
                      ? 'bg-blue-500 hover:bg-blue-600' 
                      : 'border-[#3f3f46]'
                    }
                    data-testid={`radius-preset-${preset.value}`}
                  >
                    {preset.label}
                  </Button>
                ))}
              </div>
              
              {/* Custom Radius Input */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1">
                  <Label className="text-xs text-zinc-500">Custom Radius (meters)</Label>
                  <Input
                    type="number"
                    value={customRadius}
                    onChange={(e) => setCustomRadius(e.target.value)}
                    placeholder="50-2000"
                    min={50}
                    max={2000}
                    className="bg-[#18181b] border-[#3f3f46] h-9"
                    data-testid="custom-radius-input"
                  />
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCustomRadiusSubmit}
                  disabled={!customRadius}
                  className="border-[#3f3f46] h-9"
                  data-testid="apply-custom-radius-btn"
                >
                  Apply
                </Button>
              </div>
              
              {/* Slider (optional visual control) */}
              <div className="pt-2">
                <Slider
                  value={[radius]}
                  onValueChange={([value]) => handleRadiusChange(value)}
                  min={50}
                  max={1000}
                  step={25}
                  className="w-full"
                />
                <div className="flex justify-between text-xs text-zinc-600 mt-1">
                  <span>50m</span>
                  <span>1000m</span>
                </div>
              </div>
              
              {/* Info */}
              <div className="text-xs text-zinc-500 flex items-start gap-2 p-2 bg-[#18181b] rounded">
                <AlertTriangle className="w-3 h-3 text-amber-400 mt-0.5 flex-shrink-0" />
                <span>
                  Guards must be within this radius when signing on. 
                  Smaller radius = stricter validation, larger radius = more flexibility.
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
      
      {/* Read-only info */}
      {readOnly && position && (
        <div className="text-xs text-zinc-500 flex items-center gap-4 p-2 bg-[#09090b] rounded border border-[#3f3f46]">
          <span>
            <span className="text-zinc-400">Geofence:</span>{' '}
            <span className="text-white font-medium">{radius}m</span>
          </span>
          <span>
            <span className="text-zinc-400">Location:</span>{' '}
            <span className="font-mono text-zinc-300">{position.lat.toFixed(4)}, {position.lng.toFixed(4)}</span>
          </span>
        </div>
      )}
    </div>
  );
}

export default SiteGeofenceMap;
